import { DOCUMENT } from '@angular/common';
import {
  HttpContextToken,
  type HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { APP_CONFIG } from '@core/config/app-config.tokens';
import { CSRF_HEADER_NAME, CsrfTokenService } from '@core/http/csrf-token.service';
import { isApiRequestUrl } from '@core/http/http-header-contributor';
import { catchError, type Observable, tap, throwError } from 'rxjs';

const CSRF_RETRY_DONE = new HttpContextToken<boolean>(() => false);

function applyCsrf(request: HttpRequest<unknown>, token?: string | null): HttpRequest<unknown> {
  if (token) {
    return request.clone({
      withCredentials: true,
      setHeaders: { [CSRF_HEADER_NAME]: token },
    });
  }

  return request.clone({ withCredentials: true });
}

export const csrfTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const appConfig = inject(APP_CONFIG);
  const documentRef = inject(DOCUMENT);
  const csrf = inject(CsrfTokenService);

  const isApi = isApiRequestUrl(req.url, appConfig.apiBaseUrl, documentRef.baseURI);

  if (!isApi) {
    return next(req);
  }

  const handleSuccess = tap((event: HttpEvent<unknown>) => {
    if (!(event instanceof HttpResponse)) {
      return;
    }
    csrf.applyFromHeaders(event.headers);
  });

  const token = csrf.getToken();
  const requestWithCsrf = applyCsrf(req, token);

  return next(requestWithCsrf).pipe(
    handleSuccess,
    catchError((error: HttpErrorResponse) => {
      if (error.status === 403) {
        const alreadyRetried = req.context.get(CSRF_RETRY_DONE);
        const newToken =
          error.headers.get(CSRF_HEADER_NAME) ?? error.headers.get(CSRF_HEADER_NAME.toLowerCase());

        if (newToken && !alreadyRetried) {
          csrf.setToken(newToken);

          const retryRequest = applyCsrf(
            req.clone({
              context: req.context.set(CSRF_RETRY_DONE, true),
            }),
            newToken,
          );

          return next(retryRequest).pipe(handleSuccess);
        }
      }

      return throwError(() => error);
    }),
  );
};
